import { makeAutoObservable, observable } from "mobx";
import type { TIssueDisplayProperties, TIssueQuery } from "@/types/issue";
import type {
  TIssueDisplayFilters,
  TIssueFilterState,
  TIssueLayout,
  TIssueOrderBy,
  TIssueDateOperator,
} from "@/types/issue-filter";
import type { TScopedIssuePreferencesPayload } from "@/types/issue-filter";
import type { TIssueCollectionScope, TSavedViewSnapshot } from "@/types/view";
import {
  buildIssueQueryFromFilterState,
  buildProjectIssueQueryFromFilterState,
  buildSavedViewSnapshot,
  buildSavedViewSnapshotFromFilterState,
  extractFilterStateFromSavedView,
  getDefaultDisplayProperties,
  getDefaultIssueFilters,
  getIssueCollectionKey,
  stringifySavedViewSnapshot,
} from "@/helpers/view.helper";

type ScopePersister = (payload: TScopedIssuePreferencesPayload) => Promise<unknown> | unknown;

const PERSIST_DEBOUNCE_MS = 250;

export class IssueViewPreferencesStore {
  filtersByScope = observable.map<string, TIssueFilterState>();
  selectionByScope = observable.map<string, Set<string>>();
  displayPropertiesByScope = observable.map<string, TIssueDisplayProperties>();
  savedSnapshotByScope = observable.map<string, TSavedViewSnapshot | undefined>();

  private persisters: Record<string, ScopePersister> = {};
  private persistTimers: Record<string, ReturnType<typeof setTimeout>> = {};

  constructor(_rootStore?: unknown) {
    makeAutoObservable(
      this,
      {
        registerScopePersister: false,
        unregisterScopePersister: false,
        flushScopePersister: false,
        persisters: false,
        persistTimers: false,
      } as never,
      { autoBind: true },
    );
  }

  registerScopePersister(scope: TIssueCollectionScope, persister: ScopePersister): void {
    const key = getIssueCollectionKey(scope);
    this.persisters[key] = persister;
  }

  unregisterScopePersister(scope: TIssueCollectionScope): void {
    const key = getIssueCollectionKey(scope);
    const timer = this.persistTimers[key];
    if (timer) clearTimeout(timer);
    delete this.persistTimers[key];
    delete this.persisters[key];
  }

  flushScopePersister(scope: TIssueCollectionScope): void {
    const key = getIssueCollectionKey(scope);
    const timer = this.persistTimers[key];
    if (!timer) return;
    clearTimeout(timer);
    delete this.persistTimers[key];
    this.runScopePersister(scope);
  }

  private runScopePersister(scope: TIssueCollectionScope): void {
    const key = getIssueCollectionKey(scope);
    const persister = this.persisters[key];
    if (!persister) return;
    const filters = this.getScopeFilters(scope);
    const payload: TScopedIssuePreferencesPayload = {
      filters,
      display_filters: {
        layout: filters.layout,
        ...(filters.orderBy !== "-created_at" ? { order_by: filters.orderBy } : {}),
      },
      display_properties: this.getScopeDisplayProperties(scope),
    };
    void Promise.resolve(persister(payload)).catch((err) =>
      console.warn("[issue-view-prefs] persist scope preferences failed", { scope: key }, err),
    );
  }

  private schedulePersist(scope: TIssueCollectionScope): void {
    const key = getIssueCollectionKey(scope);
    if (!this.persisters[key]) return;
    const existing = this.persistTimers[key];
    if (existing) clearTimeout(existing);
    this.persistTimers[key] = setTimeout(() => {
      delete this.persistTimers[key];
      this.runScopePersister(scope);
    }, PERSIST_DEBOUNCE_MS);
  }

  getFilters(projectId: string): TIssueFilterState {
    return this.getScopeFilters({ kind: "project", projectId });
  }

  getLayout(projectId: string): TIssueLayout {
    return this.getFilters(projectId).layout;
  }

  getSelection(projectId: string): Set<string> {
    return this.getScopeSelection({ kind: "project", projectId });
  }

  getDisplayProperties(projectId: string): TIssueDisplayProperties {
    return this.getScopeDisplayProperties({ kind: "project", projectId });
  }

  getScopeFilters(scope: TIssueCollectionScope): TIssueFilterState {
    const key = getIssueCollectionKey(scope);
    return this.filtersByScope.get(key) ?? getDefaultIssueFilters();
  }

  getScopeLayout(scope: TIssueCollectionScope): TIssueLayout {
    return this.getScopeFilters(scope).layout;
  }

  getScopeSelection(scope: TIssueCollectionScope): Set<string> {
    const key = getIssueCollectionKey(scope);
    return this.selectionByScope.get(key) ?? new Set<string>();
  }

  getScopeDisplayProperties(scope: TIssueCollectionScope): TIssueDisplayProperties {
    const key = getIssueCollectionKey(scope);
    return this.displayPropertiesByScope.get(key) ?? getDefaultDisplayProperties();
  }

  hydrateDisplayProperties(projectId: string, value: Partial<TIssueDisplayProperties>): void {
    this.hydrateScopeDisplayProperties({ kind: "project", projectId }, value);
  }

  setLayout(projectId: string, layout: TIssueLayout): void {
    this.setScopeLayout({ kind: "project", projectId }, layout);
  }

  setSearch(projectId: string, search: string): void {
    this.setScopeSearch({ kind: "project", projectId }, search);
  }

  setStateIds(projectId: string, stateIds: string[]): void {
    this.setScopeStateIds({ kind: "project", projectId }, stateIds);
  }

  setAssigneeIds(projectId: string, assigneeIds: string[]): void {
    this.setScopeAssigneeIds({ kind: "project", projectId }, assigneeIds);
  }

  setPriorities(projectId: string, priorities: string[]): void {
    this.setScopePriorities({ kind: "project", projectId }, priorities);
  }

  setTargetDate(projectId: string, targetDate: string): void {
    this.setScopeTargetDate({ kind: "project", projectId }, targetDate);
  }

  toggleSelection(projectId: string, issueId: string): void {
    this.toggleScopeSelection({ kind: "project", projectId }, issueId);
  }

  clearSelection(projectId: string): void {
    this.clearScopeSelection({ kind: "project", projectId });
  }

  buildProjectIssueQuery(projectId: string): TIssueQuery {
    return buildProjectIssueQueryFromFilterState(
      this.getScopeFilters({ kind: "project", projectId }),
    );
  }

  buildIssueQueryForScope(scope: TIssueCollectionScope): TIssueQuery {
    return buildIssueQueryFromFilterState(this.getScopeFilters(scope));
  }

  /**
   * Apply display properties WITHOUT persisting them.
   *
   * Two legitimate callers: a surface seeding what the server already holds (where
   * persisting would echo the value straight back), and a surface whose writes are
   * deliberately session-local because it gates saving behind an explicit action —
   * project views do this, pairing it with `isDirty` and an Update button.
   *
   * Anything else that means "the user changed this" wants
   * `setScopeDisplayProperties`. Reaching for this one there is how saved-view
   * column visibility silently stopped surviving a reload (NG-528).
   */
  hydrateScopeDisplayProperties(
    scope: TIssueCollectionScope,
    value: Partial<TIssueDisplayProperties>,
  ): void {
    const key = getIssueCollectionKey(scope);
    const current = this.displayPropertiesByScope.get(key);
    const next = { ...getDefaultDisplayProperties(), ...current, ...value };
    // A no-op write still hands every consumer a new object identity, which costs a
    // table re-render for nothing — and a seed that matches the defaults is the
    // common case on mount.
    if (
      current &&
      (Object.keys(next) as Array<keyof TIssueDisplayProperties>).every(
        (k) => next[k] === current[k],
      )
    )
      return;
    this.displayPropertiesByScope.set(key, next);
  }

  /**
   * Apply a user-driven display-property change and persist it — or leave it
   * session-only where the surface registered no persister.
   */
  setScopeDisplayProperties(
    scope: TIssueCollectionScope,
    value: Partial<TIssueDisplayProperties>,
  ): void {
    this.hydrateScopeDisplayProperties(scope, value);
    this.schedulePersist(scope);
  }

  hydrateScopeFilters(scope: TIssueCollectionScope, value: Partial<TIssueFilterState>): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, {
      ...getDefaultIssueFilters(),
      ...this.filtersByScope.get(key),
      ...value,
    });
  }

  setScopeLayout(scope: TIssueCollectionScope, layout: TIssueLayout): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), layout });
    this.schedulePersist(scope);
  }

  setScopeSearch(scope: TIssueCollectionScope, search: string): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), search });
    this.schedulePersist(scope);
  }

  setScopeStateIds(scope: TIssueCollectionScope, stateIds: string[]): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), stateIds });
    this.schedulePersist(scope);
  }

  setScopeAssigneeIds(scope: TIssueCollectionScope, assigneeIds: string[]): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), assigneeIds });
    this.schedulePersist(scope);
  }

  setScopePriorities(scope: TIssueCollectionScope, priorities: string[]): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), priorities });
    this.schedulePersist(scope);
  }

  setScopeTargetDate(scope: TIssueCollectionScope, targetDate: string): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), targetDate });
    this.schedulePersist(scope);
  }

  setScopeTargetDateOp(scope: TIssueCollectionScope, targetDateOp: TIssueDateOperator): void {
    const key = getIssueCollectionKey(scope);
    // Leaving "between"'s end date behind would silently narrow a later
    // before/after filter, so clear it whenever the operator stops needing it.
    const next = { ...this.getScopeFilters(scope), targetDateOp };
    if (targetDateOp !== "between") next.targetDateTo = "";
    this.filtersByScope.set(key, next);
    this.schedulePersist(scope);
  }

  setScopeTargetDateTo(scope: TIssueCollectionScope, targetDateTo: string): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), targetDateTo });
    this.schedulePersist(scope);
  }

  setScopeOverdue(scope: TIssueCollectionScope, overdue: boolean): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), overdue });
    this.schedulePersist(scope);
  }

  setScopeAllocation(scope: TIssueCollectionScope, allocation: boolean): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), allocation });
    this.schedulePersist(scope);
  }

  setScopeOrderBy(scope: TIssueCollectionScope, orderBy: TIssueOrderBy): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), orderBy });
    this.schedulePersist(scope);
  }

  setScopeGroupBy(scope: TIssueCollectionScope, groupBy: TIssueFilterState["groupBy"]): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), groupBy });
    this.schedulePersist(scope);
  }

  setScopeBoardGroupBy(
    scope: TIssueCollectionScope,
    boardGroupBy: TIssueFilterState["boardGroupBy"],
  ): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), boardGroupBy });
    this.schedulePersist(scope);
  }

  setScopeArchived(scope: TIssueCollectionScope, archived: boolean): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, { ...this.getScopeFilters(scope), archived });
    this.schedulePersist(scope);
  }

  toggleScopeSelection(scope: TIssueCollectionScope, issueId: string): void {
    const key = getIssueCollectionKey(scope);
    const selection = new Set(this.getScopeSelection(scope));
    if (selection.has(issueId)) selection.delete(issueId);
    else selection.add(issueId);
    this.selectionByScope.set(key, selection);
  }

  clearScopeSelection(scope: TIssueCollectionScope): void {
    const key = getIssueCollectionKey(scope);
    this.selectionByScope.set(key, new Set());
  }

  clearCompletedScopeSelection(scope: TIssueCollectionScope, ids: string[]): void {
    const key = getIssueCollectionKey(scope);
    const selection = new Set(this.getScopeSelection(scope));
    ids.forEach((id) => selection.delete(id));
    this.selectionByScope.set(key, selection);
  }

  hydrateFromSavedView(
    scope: TIssueCollectionScope,
    snapshot: {
      rich_filters?: Record<string, unknown>;
      display_filters?: TIssueDisplayFilters;
      display_properties?: TIssueDisplayProperties;
    },
  ): void {
    const key = getIssueCollectionKey(scope);
    this.filtersByScope.set(key, extractFilterStateFromSavedView(snapshot));
    const displayProperties = {
      ...getDefaultDisplayProperties(),
      ...snapshot.display_properties,
    };
    this.displayPropertiesByScope.set(key, displayProperties);
    this.savedSnapshotByScope.set(
      key,
      buildSavedViewSnapshot({
        ...snapshot,
        display_properties: displayProperties,
      }),
    );
  }

  resetScopeToSavedView(scope: TIssueCollectionScope): void {
    const key = getIssueCollectionKey(scope);
    const savedSnapshot = this.savedSnapshotByScope.get(key);
    if (!savedSnapshot) return;

    this.filtersByScope.set(
      key,
      extractFilterStateFromSavedView({
        rich_filters: savedSnapshot.richFilters,
        display_filters: savedSnapshot.displayFilters,
      }),
    );
    this.displayPropertiesByScope.set(key, {
      ...getDefaultDisplayProperties(),
      ...savedSnapshot.displayProperties,
    });
  }

  buildProjectViewQuery(scope: TIssueCollectionScope): TIssueQuery {
    return buildProjectIssueQueryFromFilterState(this.getScopeFilters(scope));
  }

  isDirty(scope: TIssueCollectionScope): boolean {
    const key = getIssueCollectionKey(scope);
    const savedSnapshot = this.savedSnapshotByScope.get(key);
    if (!savedSnapshot) return false;

    const currentSnapshot = buildSavedViewSnapshotFromFilterState(
      this.getScopeFilters(scope),
      this.getScopeDisplayProperties(scope),
    );

    return (
      stringifySavedViewSnapshot(savedSnapshot) !== stringifySavedViewSnapshot(currentSnapshot)
    );
  }
}
